import React from 'react';
import { IcoAlert, IcoCheck, IcoWaiting } from '../common/Icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBulkProgress } from '../../contexts/BulkProgressContext';

/**
 * The floating stack of bulk-action cards, plus the expanded progress modal.
 *
 * Rendered by Layout so it survives navigation — a batch keeps reporting while the operator works
 * on Customers, Pickups or anywhere else, which is the entire reason this feature exists.
 *
 * Deliberately NOT here: the rich Bulk Ship summary (courier breakdown, per-order failures). That
 * lives on the Orders page because it lists order numbers you act on, so "View summary" takes you
 * there rather than duplicating eighty lines of order-listing markup into the shell.
 */

const MAX_VISIBLE = 3;

// A63 — how long a finished card stays before clearing itself, and how long the send-off takes.
const AUTO_DISMISS_MS = 10000;
const BLINK_MS = 450;          // two pulses, so the eye catches that it is about to move
const FLIGHT_MS = 1100;        // then an unhurried travel to the sidebar — this is a teaching
                               // animation, not a transition; if it is quick enough to miss it has
                               // failed at the one job it exists to do
const LAND_MS = 260;           // a beat on arrival before the card is dropped from state
const STAGGER_MS = 220;        // three batches finishing together must not blur into one smear

export default function BulkProgressCards() {
  const { jobs, expandedId, minimize, expand, dismiss } = useBulkProgress();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAll, setShowAll] = React.useState(false);

  // ── A63: auto-dismiss + fly to Bulk Actions ────────────────────────────────────────────────
  //
  // A finished card used to sit bottom-right until someone closed it. Now it clears itself after
  // ten seconds — and whether it goes by that timer or by the X, it BLINKS and FLIES to the Bulk
  // Actions item in the sidebar instead of vanishing. The flight is the point: the operator sees
  // where the record went, rather than wondering whether the batch was lost.
  //
  // `flying` holds the transform for cards mid-send-off. `hovered` and page visibility hold the
  // clock: a card must never disappear while it is being read, and must never expire unseen while
  // the operator is in another tab — that would defeat the entire purpose of the animation.
  const [flying, setFlying] = React.useState({});     // jobId -> { dx, dy }
  const [hovered, setHovered] = React.useState(null);
  const cardRefs = React.useRef({});
  const timersRef = React.useRef({});                 // jobId -> timeout id(s), for cleanup

  // Every timer this component owns, cancelled on unmount. Without this a pending dismiss fires
  // into an unmounted tree after the operator navigates away.
  React.useEffect(() => () => {
    Object.values(timersRef.current).forEach((ids) => (ids || []).forEach(clearTimeout));
    timersRef.current = {};
  }, []);

  // Send a finished card on its way. Used by BOTH the timer and the X, so the two paths cannot
  // drift apart. `dismiss` is only ever called on a job that is already done — a running job has no
  // X, and the timer only starts on completion — so this can never drop a job whose rows are still
  // locked by the in-flight guard.
  // Two phases, deliberately. The first version set the transform and the transition in the SAME
  // style commit, so there was no starting frame to animate FROM — the browser jumped straight to
  // the end state and the flight was invisible. It also measured the target before the blink, so by
  // the time the card actually moved the rect was 450ms stale and it landed slightly off.
  //
  // Now: phase 'blink' renders with the transition already in place and no transform; phase 'fly'
  // is set on a later frame, having re-measured, so the browser has both a from and a to.
  const flyAway = React.useCallback((jobId) => {
    if (!jobId || flying[jobId]) return;               // already leaving; never queue two flights

    // The blink and the flight are inline styles, so the `prefers-reduced-motion` block in the
    // stylesheet below cannot reach them — it only ever covered the sidebar pulse. A 1.1s translate
    // across the viewport is precisely the kind of large movement that causes vestibular
    // discomfort, and slowing it down made that worse rather than better. Honour the setting here,
    // in JS, and fade in place instead: the card still leaves, it just does not travel.
    let reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { reduced = false; }
    if (reduced) {
      setFlying((f) => ({ ...f, [jobId]: { phase: 'fade' } }));
      const tf = setTimeout(() => {
        dismiss(jobId);
        setFlying((f) => { const n = { ...f }; delete n[jobId]; return n; });
      }, 220);
      timersRef.current[jobId] = [...(timersRef.current[jobId] || []), tf];
      return;
    }

    setFlying((f) => ({ ...f, [jobId]: { phase: 'blink' } }));

    const t1 = setTimeout(() => {
      const el = cardRefs.current[jobId];
      const target = document.querySelector('[data-nav="bulk-actions"]');
      // Measured HERE, at the instant of departure, so a stack that reflowed during the blink (a
      // sibling card leaving, the sidebar scrolling) cannot make this land off-target.
      let vec = null;
      if (el && target) {
        try {
          const a = el.getBoundingClientRect();
          const b = target.getBoundingClientRect();
          // Both must be genuinely on-screen. A collapsed sidebar or an off-canvas nav yields a
          // zero-size rect, and flying at that is worse than not flying at all.
          if (a.width > 0 && b.width > 0 && b.height > 0) {
            vec = { dx: Math.round((b.left + b.width / 2) - (a.left + a.width / 2)),
                    dy: Math.round((b.top + b.height / 2) - (a.top + a.height / 2)) };
          }
        } catch { vec = null; }                        // getBoundingClientRect can throw in odd frames
      }
      if (target && vec) target.classList.add('mc-nav-pulse');
      setFlying((f) => (f[jobId] ? { ...f, [jobId]: vec ? { phase: 'fly', ...vec } : { phase: 'fade' } } : f));

      const t2 = setTimeout(() => {
        dismiss(jobId);
        setFlying((f) => { const n = { ...f }; delete n[jobId]; return n; });
        if (target) target.classList.remove('mc-nav-pulse');
      }, FLIGHT_MS + LAND_MS);
      timersRef.current[jobId] = [...(timersRef.current[jobId] || []), t2];
    }, BLINK_MS);
    timersRef.current[jobId] = [...(timersRef.current[jobId] || []), t1];
  }, [dismiss, flying]);

  // The countdown. Restarted whenever its inputs change, which is what gives hover-pause and
  // tab-pause for free: leaving the card, or coming back to the tab, re-runs this effect and the
  // full ten seconds starts again rather than resuming a partial one. Simpler than tracking elapsed
  // time, and erring toward leaving the card up longer is the safe direction.
  const doneIds = Object.values(jobs).filter((j) => j.done).map((j) => j.jobId).join(',');
  const [visTick, forceTick] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    if (typeof document !== 'undefined' && document.hidden) return;   // tab hidden — clock stops
    const ids = doneIds ? doneIds.split(',') : [];
    const started = [];
    ids.forEach((id, i) => {
      if (!id) return;
      if (expandedId === id) return;        // maximized: the operator is reading it, no timer
      if (hovered === id) return;           // pointer on it: same reason
      if (flying[id]) return;               // already leaving
      const t = setTimeout(() => flyAway(id), AUTO_DISMISS_MS + i * STAGGER_MS);
      started.push(t);
    });
    return () => started.forEach(clearTimeout);
    // `visTick` is a real dependency, not decoration: without it the visibilitychange listener
    // below re-rendered but this effect never re-ran, so the tab-pause silently did nothing.
  }, [doneIds, expandedId, hovered, flying, flyAway, visTick]);

  // Re-run the countdown when the tab is hidden or shown, so the clock genuinely stops and restarts
  // rather than being read once at mount.
  React.useEffect(() => {
    const onVis = () => forceTick();
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Newest first: the thing just started is the thing being watched.
  const all = Object.values(jobs).sort((a, b) => String(b.jobId).localeCompare(String(a.jobId)));
  const cards = all.filter((j) => j.minimized || j.done);
  const expanded = expandedId ? jobs[expandedId] : null;
  const visible = showAll ? cards : cards.slice(0, MAX_VISIBLE);
  const hidden = cards.length - visible.length;

  const pct = (j) => (j.total > 0 ? Math.min(100, Math.round(((j.current || 0) / j.total) * 100)) : 0);

  // A finished card must not still read "Creating shipments". `label` is written for the running
  // state and stays in the present tense, so a completed batch looked like it was still working
  // even while showing "4 done, 1 failed" underneath it.
  const cardTitle = (j) => {
    if (!j.done) return j.label;
    const ship = j.type === 'bulk_ship';
    if (j.fail > 0) return ship ? 'Shipping finished with errors' : 'Labels finished with errors';
    return ship ? 'Shipping complete' : 'Labels ready';
  };

  const viewSummary = (j) => {
    // The summary lives on Orders. Send the operator there and let the page open it; dismiss the
    // card either way so it cannot be clicked twice.
    if (!location.pathname.startsWith('/dashboard/orders')) navigate('/dashboard/orders');
    // The finished job travels WITH the event. The Orders page may have been unmounted while this
    // ran (that is the feature), so it cannot be expected to still hold the result in its own state
    // — it rebuilds the summary from job.summary.details, which carries order numbers already.
    window.dispatchEvent(new CustomEvent('mc:bulk-view-summary', { detail: { jobId: j.jobId, job: j.job, type: j.type } }));
    dismiss(j.jobId);
  };

  return (
    <>
      {/* ── expanded progress modal ─────────────────────────────────────────────────────── */}
      {expanded && (
        <div style={S.overlay}>
          <style>{`@keyframes mc-indet{0%{left:-40%}100%{left:100%}}`}</style>
          <div style={S.modal}>
            <button
              style={S.minBtn}
              title={expanded.done ? 'Back to the card' : 'Minimise — the batch keeps running and you can carry on working'}
              onClick={() => minimize(expanded.jobId)}
            >–</button>
            {/* Deliberately no ✕ here. There is no way to cancel a batch mid-flight, so a close
                button would either lie about what it does or lose track of a running job. */}
            <h3 style={S.title}>
              {expanded.done
                ? (expanded.fail > 0 ? <IcoAlert size={14} color="#f59e0b" /> : <IcoCheck size={14} color="#16a34a" />)
                : <IcoWaiting size={14} color="#64748b" />} {cardTitle(expanded)}{expanded.done ? '' : '…'}
            </h3>
            <p style={S.sub}>
              {expanded.total > 1 ? 'Bulk action' : 'Single order'} — {expanded.current}/{expanded.total}
            </p>
            <div style={S.track}>
              <div style={{ ...S.bar, width: `${expanded.done ? 100 : pct(expanded)}%`, background: expanded.fail > 0 ? '#f59e0b' : (expanded.done ? '#16a34a' : '#2563eb') }} />
            </div>
            {/* A finished batch reports what happened; a running one reports where it is. */}
            <p style={S.pText}>
              {expanded.done
                ? `${expanded.ok} done${expanded.fail > 0 ? `, ${expanded.fail} failed` : ''}`
                : `Processing ${expanded.current} of ${expanded.total}…`}
            </p>
            {(expanded.from || expanded.via) && (
              <p style={S.meta}>
                {expanded.from ? <>from <b style={{ color: '#334155' }}>{expanded.from}</b></> : null}
                {expanded.via ? <> · via <b style={{ color: '#334155' }}>{expanded.via}</b></> : null}
              </p>
            )}
            {expanded.done ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button style={{ ...S.minLink, marginTop: 0 }} onClick={() => { minimize(expanded.jobId); flyAway(expanded.jobId); }}>
                  Close
                </button>
                {expanded.mine && (
                  <button style={S.summaryBtn} onClick={() => viewSummary(expanded)}>View summary →</button>
                )}
                {/* A88 — failures: let the seller see whether it is us, the courier or their setup. */}
                {expanded.fail > 0 && (
                  <button style={{ ...S.minLink, marginTop: 0 }} onClick={() => { minimize(expanded.jobId); navigate('/dashboard/status'); }}>
                    Check system status
                  </button>
                )}
              </div>
            ) : (
              <button style={S.minLink} onClick={() => minimize(expanded.jobId)}>
                Minimise and keep working
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── floating stack ──────────────────────────────────────────────────────────────── */}
      {/* Keyframes live with the component that uses them. `mc-nav-pulse` is added to the sidebar
          item for the duration of a flight, so the card visibly lands somewhere rather than just
          disappearing near it. */}
      <style>{`
        @keyframes mc-card-blink { 0%,100%{opacity:1} 25%,75%{opacity:.35} }
        @keyframes mc-card-fade  { to { opacity:0; transform:scale(.9) } }
        @keyframes mc-nav-pulse-kf {
          0%      { background:transparent; transform:none }
          55%     { background:#dbeafe;     transform:scale(1.05) }
          75%     { background:#bfdbfe;     transform:scale(1.09) }
          100%    { background:transparent; transform:none }
        }
        /* Runs for the whole flight plus the landing beat, so the destination is already lit when
           the card gets there — a pulse that finished first teaches nothing. */
        .mc-nav-pulse { animation: mc-nav-pulse-kf ${FLIGHT_MS + LAND_MS}ms ease-in-out; border-radius:8px; }
        @media (prefers-reduced-motion: reduce) {
          .mc-nav-pulse { animation: none; }
        }
      `}</style>
      {cards.length > 0 && (
        <div style={S.stack}>
          {hidden > 0 && !showAll && (
            <button style={S.more} onClick={() => setShowAll(true)}>+{hidden} more running</button>
          )}
          {showAll && cards.length > MAX_VISIBLE && (
            <button style={S.more} onClick={() => setShowAll(false)}>Show fewer</button>
          )}
          {visible.map((j) => {
            const fly = flying[j.jobId];
            return (
            <div
              key={j.jobId}
              ref={(el) => { if (el) cardRefs.current[j.jobId] = el; else delete cardRefs.current[j.jobId]; }}
              // Pointer on the card stops the countdown. Reading "18 done, 2 failed" and having it
              // vanish mid-sentence is hostile, and this is the cheapest possible guard against it.
              onMouseEnter={() => j.done && setHovered(j.jobId)}
              onMouseLeave={() => setHovered((h) => (h === j.jobId ? null : h))}
              style={{
                ...S.card,
                borderColor: j.done ? (j.fail > 0 ? '#fcd34d' : '#86efac') : '#e2e8f0',
                ...(fly ? {
                  // The transition is present from the BLINK phase onward, so when the transform
                  // arrives a frame later the browser has something to animate from. `pointerEvents`
                  // off stops a stray click landing on a card already on its way out.
                  //
                  // Easing is a gentle ease-in-out rather than a snap: this animation exists to be
                  // followed by eye all the way to the sidebar, not to get the card off screen.
                  transition: fly.phase === 'fade'
                    ? 'opacity 200ms ease-out, transform 200ms ease-out'
                    : `transform ${FLIGHT_MS}ms cubic-bezier(.33,0,.2,1), opacity ${FLIGHT_MS}ms ease-in`,
                  ...(fly.phase === 'blink' ? { animation: `mc-card-blink ${BLINK_MS}ms ease-in-out` } : {}),
                  ...(fly.phase === 'fly' ? {
                    // Stays large for most of the journey and shrinks late, so the eye can track it
                    // rather than losing a speck the moment it leaves.
                    transform: `translate(${fly.dx}px, ${fly.dy}px) scale(0.32)`,
                    opacity: 0.15,
                  } : {}),
                  ...(fly.phase === 'fade' ? { opacity: 0, transform: 'scale(0.92)' } : {}),
                  pointerEvents: 'none',
                } : {}),
              }}
            >
              <div style={S.cardTop}>
                <button
                  style={S.cardMain}
                  // A finished card now OPENS instead of jumping straight to the summary page. That
                  // is what gives "no auto-dismiss while maximized" something to govern, and it is
                  // where a failed batch can be read without leaving the page. The summary is still
                  // one click away, from inside.
                  onClick={() => (j.mine ? expand(j.jobId) : (j.done ? flyAway(j.jobId) : null))}
                  title={j.done ? 'Open — the countdown pauses while it is open' : (j.mine ? 'Open' : `Started by ${j.byName}`)}
                >
                  <span style={S.cardLabel}>
                    {j.done
                      ? (j.fail > 0 ? <IcoAlert size={13} color="#f59e0b" /> : <IcoCheck size={13} color="#16a34a" />)
                      : <IcoWaiting size={13} color="#64748b" />} {cardTitle(j)}
                    {!j.mine && j.byName ? <span style={S.by}> · {j.byName}</span> : null}
                  </span>
                  <span style={S.count}>
                    {j.done
                      ? `${j.ok} done${j.fail > 0 ? `, ${j.fail} failed` : ''}`
                      : `${j.current}/${j.total}`}
                  </span>
                </button>
                {/* Dismiss ONLY once the batch has finished.
                    Closing the card does NOT cancel anything — the job is server-side and keeps
                    running — but it removes the job from tracking, which unlocks all of its rows
                    and lets a second batch be started over orders still being shipped. Every one
                    of those comes back as a 409 and lands in the summary as a phantom failure,
                    which is the exact problem the in-flight lock exists to prevent. So while it
                    runs there is nothing to close: minimise it instead. */}
                {j.done ? (
                  <button style={S.x} title="Dismiss" onClick={() => flyAway(j.jobId)}>✕</button>
                ) : (
                  <span style={S.xOff} title="This batch is still running — it cannot be dismissed until it finishes">✕</span>
                )}
              </div>
              {!j.done && (
                <div style={S.trackSm}>
                  <div style={{ ...S.barSm, width: `${pct(j)}%`, background: j.fail > 0 ? '#f59e0b' : '#2563eb' }} />
                </div>
              )}
              {j.done && j.mine && <div style={S.viewHint}>Open →</div>}
            </div>
            );
          })}
        </div>
      )}
    </>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400 },
  modal: { position: 'relative', background: '#fff', borderRadius: 14, padding: '22px 24px 18px', width: 360, maxWidth: '92vw', textAlign: 'center', boxShadow: 'var(--mc-e4)' },
  minBtn: { position: 'absolute', top: 10, right: 12, width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 18, lineHeight: '24px', cursor: 'pointer' },
  title: { margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#0f172a' },
  sub: { margin: '0 0 10px', fontSize: 13, color: '#64748b' },
  track: { height: 8, background: '#eef2f7', borderRadius: 6, overflow: 'hidden' },
  bar: { height: '100%', borderRadius: 6, transition: 'width var(--mc-t-slow) var(--mc-ease)' },
  pText: { margin: '8px 0 0', fontSize: 12.5, color: '#475569' },
  meta: { fontSize: 12, color: '#64748b', marginTop: 4 },
  summaryBtn: { flex: 1, background: '#2563eb', border: '1px solid #2563eb', borderRadius: 9, padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer' },
  minLink: { marginTop: 14, width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' },

  // Sits above the bulk-selection bar, which is fixed to the bottom when rows are selected.
  stack: { position: 'fixed', right: 18, bottom: 96, zIndex: 1300, display: 'flex', flexDirection: 'column', gap: 8, width: 288, maxWidth: 'calc(100vw - 36px)' },
  more: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 9, padding: '7px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 11, boxShadow: 'var(--mc-e2)', padding: '9px 10px 10px' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 6 },
  cardMain: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' },
  cardLabel: { fontSize: 12.5, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 228 },
  by: { color: '#64748b', fontWeight: 500 },
  count: { fontSize: 11.5, color: '#64748b' },
  x: { background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 },
  xOff: { color: '#e2e8f0', fontSize: 13, padding: '2px 4px', lineHeight: 1, cursor: 'not-allowed', userSelect: 'none' },
  trackSm: { height: 5, background: '#eef2f7', borderRadius: 4, overflow: 'hidden', marginTop: 7 },
  barSm: { height: '100%', borderRadius: 4, transition: 'width var(--mc-t-slow) var(--mc-ease)' },
  viewHint: { marginTop: 6, fontSize: 11.5, fontWeight: 600, color: '#2563eb' },
};
